import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Calendar,
  MapPin,
  Camera,
  Video,
  Edit3,
  Send,
  User,
  Star,
  ShieldCheck,
  MessageSquare,
} from "lucide-react";
import { roleMeta, money } from "@/utils/fjMeta";
import linkifyText from "@/utils/linkifyText";

/**
 * JobDetailsCrewModal — high-fidelity Find Jobs job-details modal.
 * Renders BOTH states from one component, driven by the job's positions count:
 *   1 position  → single-role "THE ROLE"
 *   2+ positions → multi-role "OPEN POSITIONS" (per-role Apply; filled disabled)
 *
 * Portals to document.body so it escapes the page's `.cd-main` stacking context.
 *
 * Props:
 *   job           full job payload from GET /api/jobs/details/{id}
 *   isOpen        bool
 *   onClose()     close the modal
 *   onApplyRole(role)  apply scoped to a role (reuses page apply flow + gating)
 *   onAskQuestion()    open Messages with the poster (omit to hide the button)
 */

// ---- Tokens (exact, per handoff README) ----------------------------------
const C = {
  modal: "#12162A",
  scrim: "rgba(8,11,20,.66)",
  hero: "linear-gradient(125deg,#572B7C,#78287C 60%,#0C1120)",
  aqua: "#28C0D0",
  mint: "#67C6B4",
  ink: "#0C1120",
  yellow: "#FCD34D",
  white: "#fff",
  label: "rgba(255,255,255,.45)",
};

// role → lucide icon (photographer Camera, videographer Video, editor Edit3,
// drone Send, assistant User; Camera default)
function iconForRole(role) {
  const r = (role || "").toLowerCase();
  if (r.includes("video")) return Video;
  if (r.includes("edit")) return Edit3;
  if (r.includes("drone")) return Send;
  if (r.includes("assist")) return User;
  return Camera;
}

function prettyType(t) {
  if (!t) return null;
  return String(t)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function shortLoc(loc) {
  if (!loc) return "";
  const parts = String(loc)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.slice(-2).join(", ");
}

function fmtDate(d) {
  if (!d) return "Date TBD";
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const SECTION_LABEL = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: ".6px",
  color: C.label,
};

const JobDetailsCrewModal = ({ job, isOpen, onClose, onApplyRole, onAskQuestion }) => {
  const [reduced, setReduced] = useState(false);

  // prefers-reduced-motion
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener("change", apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", apply) : mq.removeListener(apply);
    };
  }, []);

  // Esc to close while open
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !job) return null;

  // ---- Derive positions from crew_members ---------------------------------
  const crewMembers = job.crew_members || job.crewMembers || [];

  // Unique job dates (across all members incl. filled) → multi-day detection
  const dateSet = new Set();
  crewMembers.forEach((m) =>
    (m.dateAssignments || []).forEach((a) => {
      if (a && a.date && (a.hours || 0) > 0) dateSet.add(a.date);
    })
  );
  const totalDays = dateSet.size || (Array.isArray(job.dates) ? job.dates.length : 0) || 1;
  const isMultiDay = totalDays > 1;

  let positions;
  if (crewMembers.length === 0) {
    // Simple job with no crew_members array — synthesize one position.
    const rate = Number(job.pay_rate || job.ratePerHour || job.rate || 0);
    const hours = Number(job.hours || 0);
    const gross = Number(job.total_amount || job.total || hours * rate || 0);
    positions = [
      {
        role: job.type || job.job_type || (job.required_roles || [])[0] || "photographer",
        hours,
        rate,
        days: 1,
        gross,
        status: "open",
      },
    ];
  } else {
    positions = crewMembers.map((m) => {
      const rate = Number(m.ratePerHour || m.rate || job.pay_rate || 0);
      const dayAssigns = (m.dateAssignments || []).filter((a) => (a.hours || 0) > 0);
      const days = dayAssigns.length || 1;
      const hours = Number(
        m.hours || dayAssigns.reduce((s, a) => s + (a.hours || 0), 0) || 0
      );
      // subtotal already includes days when present
      const gross = Number(m.subtotal || hours * rate || 0);
      const filled = !!(
        m.hired ||
        m.hired_user_id ||
        m.hiredUserName ||
        m.hired_user_name ||
        m.hired_user_number
      );
      return { role: m.role, hours, rate, days, gross, status: filled ? "filled" : "open" };
    });
  }

  const firstOpenIdx = positions.findIndex((p) => p.status === "open");
  positions = positions.map((p, i) => ({ ...p, recommended: i === firstOpenIdx }));

  const openCount = positions.filter((p) => p.status === "open").length;
  const isMulti = positions.length > 1;

  const payoutOf = (p) => Math.floor((p.gross || 0) * 0.98);
  // Crew see their own per-hour rate (after the 2% platform fee), not the gross rate.
  const crewRateOf = (p) => (p.rate || 0) * 0.98;
  const subLineOf = (p) =>
    p.days > 1
      ? `${p.days} days · ${Math.round((p.hours / p.days) * 10) / 10} hrs/day × ${money(crewRateOf(p))}/hr`
      : `${p.hours} hrs × ${money(crewRateOf(p))}/hr`;

  // ---- Hero meta ----------------------------------------------------------
  const jobId =
    job.job_number ||
    job.jobNumber ||
    job.mysql_job_id ||
    `SZ-${String(job.id || "").slice(0, 6).toUpperCase()}`;

  const applicantCount = Number(job.applicants ?? job.applicant_count ?? job.applicantCount ?? 0);
  const isPopular = applicantCount >= 5;

  const owner = job.owner || {};
  const posterName = owner.name || job.owner_name || "Studio";
  const posterInitials =
    (posterName
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .slice(0, 2) || "S").toUpperCase();
  const posterRating = owner.rating ?? owner.review_average ?? job.owner_rating;
  const posterJobs = owner.jobsCount ?? owner.jobs_count ?? owner.completed_jobs ?? job.owner_jobs_count;

  const dateLabel = fmtDate(job.date || job.jobDate || job.job_date);
  const locLabel = shortLoc(job.location_address || job.location || job.job_location);

  const brief = job.description || job.brief || "No description provided.";

  // ---- Tags ---------------------------------------------------------------
  const neutralTags = [];
  const typeTag = prettyType(job.type || job.job_type);
  if (typeTag) neutralTags.push(typeTag);
  const requiresWwcc = !!(job.requires_wwcc || job.requiresWWCC);

  // ---- Shared style helpers ----------------------------------------------
  const ghostBtn = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: 13,
    borderRadius: 12,
    background: "rgba(255,255,255,.06)",
    border: "1px solid rgba(255,255,255,.16)",
    color: C.white,
    fontWeight: 600,
    fontSize: 13.5,
    cursor: "pointer",
    fontFamily: "'Work Sans', sans-serif",
    transition: "filter .16s ease, transform .16s ease",
  };
  const primaryBtn = {
    padding: 13,
    borderRadius: 12,
    background: C.aqua,
    border: "none",
    color: C.ink,
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    boxShadow: "0 12px 28px -10px rgba(40,192,208,.65)",
    fontFamily: "'Poppins', sans-serif",
    transition: "filter .16s ease, transform .16s ease",
  };

  const overlay = (
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose && onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 4000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: C.scrim,
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
        fontFamily: "'Work Sans', sans-serif",
      }}
    >
      <style>{`
        @keyframes jdcm-spin { to { transform: rotate(360deg); } }
        @keyframes jdcm-in { from { opacity:0; transform: translateY(14px) scale(.98); } to { opacity:1; transform:none; } }
        .jdcm-lift:hover { filter: brightness(1.06); transform: translateY(-1px); }
      `}</style>

      <div
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "90dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: C.modal,
          border: "1px solid rgba(255,255,255,.12)",
          borderRadius: 22,
          boxShadow: "0 40px 90px -30px rgba(0,0,0,.85)",
          animation: reduced ? "none" : "jdcm-in 240ms cubic-bezier(.2,.85,.25,1)",
        }}
      >
        {/* ============ HERO (sticky) ============ */}
        <div
          style={{
            position: "relative",
            flexShrink: 0,
            padding: "24px 26px 22px",
            background: C.hero,
            overflow: "hidden",
          }}
        >
          <img
            src="/assets/website/symbol-white.svg"
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              right: -40,
              top: -30,
              width: 180,
              height: 180,
              opacity: 0.07,
              animation: reduced ? "none" : "jdcm-spin 80s linear infinite",
              pointerEvents: "none",
            }}
          />

          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "rgba(255,255,255,.12)",
              border: "1px solid rgba(255,255,255,.18)",
              color: C.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 3,
            }}
          >
            <X size={17} />
          </button>

          {/* Meta row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              marginBottom: 13,
              flexWrap: "wrap",
              position: "relative",
              zIndex: 1,
            }}
          >
            <span
              className="cd-mono"
              style={{ fontSize: 11.5, color: "rgba(255,255,255,.7)" }}
            >
              {jobId}
            </span>
            {isMultiDay && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.white,
                  background: "rgba(255,255,255,.16)",
                  padding: "3px 9px",
                  borderRadius: 999,
                }}
              >
                Advanced · {totalDays} days
              </span>
            )}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11,
                fontWeight: 700,
                color: C.yellow,
                background: "rgba(0,0,0,.22)",
                padding: "3px 9px",
                borderRadius: 999,
              }}
            >
              {isPopular ? (
                <>
                  <Star size={11} fill="currentColor" stroke="none" />
                  Popular
                </>
              ) : (
                `${openCount} role${openCount === 1 ? "" : "s"} open`
              )}
            </span>
          </div>

          {/* Title */}
          <div
            className="cd-display"
            style={{
              position: "relative",
              zIndex: 1,
              fontWeight: 800,
              fontSize: 27,
              color: C.white,
              letterSpacing: "-.8px",
              lineHeight: 1.08,
            }}
          >
            {job.title}
          </div>

          {/* Poster row */}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              gap: 9,
              marginTop: 11,
            }}
          >
            <span
              className="cd-display"
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                background: "linear-gradient(135deg,#28C0D0,#67C6B4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: C.ink,
                fontWeight: 700,
                fontSize: 10,
                flex: "0 0 auto",
              }}
            >
              {posterInitials}
            </span>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,.85)" }}>
              {posterName}
              {posterRating != null && (
                <>
                  {" · "}
                  <span style={{ color: C.white }}>{posterRating} ★</span>
                </>
              )}
              {posterJobs != null && ` · ${posterJobs} jobs`}
            </span>
          </div>

          {/* Facts row */}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              gap: 18,
              marginTop: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontSize: 12.5,
                color: "rgba(255,255,255,.82)",
              }}
            >
              <Calendar size={15} color={C.aqua} />
              {dateLabel}
            </div>
            {locLabel && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  fontSize: 12.5,
                  color: "rgba(255,255,255,.82)",
                }}
              >
                <MapPin size={15} color={C.aqua} />
                {locLabel}
              </div>
            )}
          </div>
        </div>

        {/* ============ BODY (scrollable) ============ */}
        <div style={{ padding: "20px 26px", flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {!isMulti ? (
            /* ---- THE ROLE (single) ---- */
            <>
              <div style={{ ...SECTION_LABEL, marginBottom: 10 }}>THE ROLE</div>
              {positions.map((p, i) => {
                const Icon = iconForRole(p.role);
                return (
                  <div
                    key={i}
                    style={{
                      border: "1px solid rgba(40,192,208,.3)",
                      background: "rgba(40,192,208,.06)",
                      borderRadius: 14,
                      padding: 16,
                      marginBottom: 18,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <span
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 10,
                            background: "rgba(40,192,208,.16)",
                            color: C.aqua,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                          }}
                        >
                          <Icon size={17} />
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div
                            className="cd-display"
                            style={{ fontSize: 14.5, fontWeight: 700, color: C.white }}
                          >
                            {roleMeta(p.role).label}
                          </div>
                          <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.5)" }}>
                            {subLineOf(p)}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flex: "0 0 auto" }}>
                        <div style={{ fontSize: 10.5, color: C.label }}>You earn</div>
                        <div
                          className="cd-display"
                          style={{
                            fontWeight: 800,
                            fontSize: 20,
                            color: C.mint,
                            letterSpacing: "-.4px",
                          }}
                        >
                          {money(payoutOf(p))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            /* ---- OPEN POSITIONS (multi) ---- */
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <span style={SECTION_LABEL}>OPEN POSITIONS</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.yellow }}>
                  {openCount} of {positions.length} available
                </span>
              </div>

              {positions.map((p, i) => {
                const Icon = iconForRole(p.role);
                const isFilled = p.status === "filled";
                const isPrimary = p.recommended;

                const cardStyle = isFilled
                  ? {
                      border: "1px solid rgba(255,255,255,.08)",
                      borderRadius: 14,
                      padding: "14px 16px",
                      marginBottom: 10,
                      opacity: 0.6,
                    }
                  : isPrimary
                  ? {
                      border: "1px solid rgba(40,192,208,.3)",
                      background: "rgba(40,192,208,.06)",
                      borderRadius: 14,
                      padding: "15px 16px",
                      marginBottom: 10,
                    }
                  : {
                      border: "1px solid rgba(255,255,255,.1)",
                      borderRadius: 14,
                      padding: "15px 16px",
                      marginBottom: 10,
                    };

                const chipStyle = isFilled
                  ? { background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.5)" }
                  : isPrimary
                  ? { background: "rgba(40,192,208,.16)", color: C.aqua }
                  : { background: "rgba(103,198,180,.16)", color: C.mint };

                return (
                  <div key={i} style={cardStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <span
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            ...chipStyle,
                          }}
                        >
                          <Icon size={17} />
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div
                            className="cd-display"
                            style={{ fontSize: 14, fontWeight: 700, color: C.white }}
                          >
                            {roleMeta(p.role).label}
                          </div>
                          <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.5)" }}>
                            {subLineOf(p)}
                          </div>
                        </div>
                      </div>
                      {isFilled ? (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: C.mint,
                            background: "rgba(103,198,180,.16)",
                            padding: "4px 10px",
                            borderRadius: 999,
                            flex: "0 0 auto",
                          }}
                        >
                          Filled
                        </span>
                      ) : (
                        <div style={{ textAlign: "right", flex: "0 0 auto" }}>
                          <div style={{ fontSize: 10.5, color: C.label }}>You earn</div>
                          <div
                            className="cd-display"
                            style={{
                              fontWeight: 800,
                              fontSize: 18,
                              color: C.mint,
                              letterSpacing: "-.4px",
                            }}
                          >
                            {money(payoutOf(p))}
                          </div>
                        </div>
                      )}
                    </div>

                    {!isFilled && (
                      <button
                        className="jdcm-lift"
                        onClick={() => onApplyRole && onApplyRole(p.role)}
                        style={{
                          width: "100%",
                          marginTop: 13,
                          padding: 11,
                          borderRadius: 11,
                          fontSize: 13,
                          cursor: "pointer",
                          transition: "filter .16s ease, transform .16s ease",
                          ...(isPrimary
                            ? {
                                background: C.aqua,
                                border: "none",
                                color: C.ink,
                                fontWeight: 700,
                                fontFamily: "'Poppins', sans-serif",
                              }
                            : {
                                background: "rgba(255,255,255,.06)",
                                border: "1px solid rgba(255,255,255,.16)",
                                color: C.white,
                                fontWeight: 600,
                              }),
                        }}
                      >
                        Apply for {roleMeta(p.role).label}
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* ============ THE BRIEF ============ */}
          <div style={{ ...SECTION_LABEL, marginTop: 8, marginBottom: 8 }}>THE BRIEF</div>
          <div
            style={{
              fontSize: 13.5,
              color: "rgba(255,255,255,.75)",
              lineHeight: 1.65,
              marginBottom: 16,
              whiteSpace: "pre-wrap",
            }}
          >
            {linkifyText(brief)}
          </div>

          {/* ============ TAGS ============ */}
          {(neutralTags.length > 0 || requiresWwcc) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
              {neutralTags.map((t, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 11.5,
                    color: C.white,
                    background: "rgba(255,255,255,.06)",
                    border: "1px solid rgba(255,255,255,.1)",
                    padding: "6px 11px",
                    borderRadius: 999,
                  }}
                >
                  {t}
                </span>
              ))}
              {requiresWwcc && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 11.5,
                    color: C.mint,
                    background: "rgba(103,198,180,.12)",
                    border: "1px solid rgba(103,198,180,.25)",
                    padding: "6px 11px",
                    borderRadius: 999,
                  }}
                >
                  <ShieldCheck size={12} />
                  WWCC required
                </span>
              )}
            </div>
          )}
        </div>

        {/* ============ FOOTER (sticky) ============ */}
        <div style={{ flexShrink: 0, padding: "15px 22px calc(15px + env(safe-area-inset-bottom))", borderTop: "1px solid rgba(255,255,255,.08)", background: C.modal }}>
          {!isMulti ? (
            <div style={{ display: "flex", gap: 10 }}>
              {onAskQuestion && (
                <button className="jdcm-lift" onClick={onAskQuestion} style={{ ...ghostBtn, flex: 1 }}>
                  <MessageSquare size={16} />
                  Ask a question
                </button>
              )}
              <button
                className="jdcm-lift"
                onClick={() => onApplyRole && onApplyRole(positions[0]?.role)}
                style={{ ...primaryBtn, flex: onAskQuestion ? 1.6 : 1 }}
              >
                Apply for this role
              </button>
            </div>
          ) : (
            onAskQuestion && (
              <button className="jdcm-lift" onClick={onAskQuestion} style={{ ...ghostBtn, width: "100%" }}>
                <MessageSquare size={16} />
                Ask a question
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default JobDetailsCrewModal;
